import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAuth } from "contexts/AuthContext";
import { useOwnUndoRedo } from "../useOwnUndoRedo";
import { useRevertHistoryEntry } from "../useRevertHistoryEntry";
import { useUndoRedoShortcuts } from "../../../hooks/useUndoRedoShortcuts";
import { historySubjectOf } from "../../utils/historySubjectOf";
import type { ConfirmActionModalProps } from "../../../ConfirmActionModal";
import type {
  HistorySubject,
  RevertHistoryEntryArgs,
  RevertHistoryEntrySuccess,
} from "../../types";
import type { UseHistoryUndoArgs, UseHistoryUndoResult } from "./types";

export const useHistoryUndo = ({
  projectId,
  isDiagramsMode,
  selectedFloorPlanId,
  selectedDiagramId,
  onReverted,
  isOwnUndoAvailable,
  isOwnUndoPaused,
}: UseHistoryUndoArgs): UseHistoryUndoResult => {
  const { t } = useTranslation();
  const { user } = useAuth();

  // A revert that reaches other plans waits here for the user's yes.
  const [pendingRevert, setPendingRevert] =
    useState<RevertHistoryEntryArgs | null>(null);

  const handleReverted = useCallback(
    ({ entries }: RevertHistoryEntrySuccess) => {
      for (const { floorPlanId, diagramId, changes } of entries) {
        onReverted({
          subject: historySubjectOf({ floorPlanId, diagramId }),
          changes,
        });
      }
    },
    [onReverted],
  );

  const { revert: revertNow, revertingEntryId } = useRevertHistoryEntry({
    onReverted: handleReverted,
  });

  const revert = useCallback(
    (args: RevertHistoryEntryArgs) => {
      if (args.planCount > 1) {
        setPendingRevert(args);
        return;
      }
      revertNow(args);
    },
    [revertNow],
  );

  const subject = useMemo((): HistorySubject | null => {
    if (isDiagramsMode) {
      return selectedDiagramId === null
        ? null
        : { kind: "diagram", diagramId: selectedDiagramId };
    }
    return selectedFloorPlanId === null
      ? null
      : { kind: "plan", floorPlanId: selectedFloorPlanId };
  }, [isDiagramsMode, selectedDiagramId, selectedFloorPlanId]);

  const revertConfirmProps = useMemo(
    (): ConfirmActionModalProps => ({
      isOpen: pendingRevert !== null,
      onClose: () => setPendingRevert(null),
      onConfirm: () => {
        if (pendingRevert !== null) revertNow(pendingRevert);
      },
      action: "undo",
      title: t("smartSurveyor.history.revertManyPlans.title", {
        count: pendingRevert?.planCount ?? 0,
      }),
      description: t("smartSurveyor.history.revertManyPlans.description"),
    }),
    [pendingRevert, revertNow, t],
  );

  const ownSubject = isOwnUndoAvailable ? subject : null;
  const ownUndoRedo = useOwnUndoRedo({
    projectId,
    subject: ownSubject,
    currentUserEmail: user?.email ?? null,
    revertingEntryId,
    revert,
    isPaused: isOwnUndoPaused,
  });

  useUndoRedoShortcuts({
    onUndo: ownUndoRedo.undo.run,
    onRedo: ownUndoRedo.redo.run,
    isDisabled: ownSubject === null || isOwnUndoPaused,
  });

  return {
    subject,
    revert,
    revertingEntryId,
    revertConfirmProps,
    ownUndoRedo: ownSubject === null ? undefined : ownUndoRedo,
  };
};
